import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_TINYCMS_ROOT_FILES,
  GET_TINYCMS_SITE,
  UPDATE_TINYCMS_ROOT_FILES,
  RESTORE_TINYCMS_ROOT_FILES,
} from '../../graphql';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';

/**
 * Site settings → Verification & ads.txt: what the site serves for other
 * services. Verification (Google Search Console, Bing, Pinterest, Meta,
 * Yandex) is a <meta> tag on the home page or a file at the site's root, on
 * every plan. ads.txt / app-ads.txt (who may sell the site's ad space) are Pro;
 * text saved on Pro is kept after a move off it but not served.
 *
 * Changes go live at once (no publishing). Every save is a version that can be
 * restored. Admins edit; members see it read-only (the server enforces it).
 * The server pulls the code out of whatever was pasted (the whole tag, the
 * code, a file name or address, Bing's XML).
 */

type Method = 'meta' | 'file';

interface Verification {
  service: string;
  method: Method;
  value: string;
  label?: string;
  address?: string;
  metaTag?: string;
}

interface Version {
  version: number;
  note?: string | null;
  changedAt: string;
  changedVia?: string;
  changedBy?: string | null;
}

const PLAN_LINK = '/settings?section=billing';
const METHOD_LABEL: Record<Method, string> = { meta: 'HTML tag', file: 'HTML file' };

/** What each service hands you, as a placeholder for the paste box. */
const META_NAME: Record<string, string> = {
  google: 'google-site-verification',
  bing: 'msvalidate.01',
  pinterest: 'p:domain_verify',
  facebook: 'facebook-domain-verification',
  yandex: 'yandex-verification',
};
const placeholder = (service: string, method: Method) =>
  method === 'file'
    ? service === 'google'
      ? 'google1a2b3c4d5e6f7a8b.html'
      : 'The code, or everything in BingSiteAuth.xml'
    : `<meta name="${META_NAME[service] || 'verification'}" content="…">`;

/** The stored shape the server accepts back (it refuses the display fields). */
const stored = (v: Verification) => ({ service: v.service, method: v.method, value: v.value });

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

export function RootFilesSettings() {
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_ROOT_FILES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: siteData } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const view = data?.tinycmsRootFiles;
  const versions: Version[] = data?.tinycmsRootFileVersions || [];
  const canManage = Boolean(view?.canManage);
  const adsAllowed = Boolean(view?.adsAllowed);
  const siteUrl: string | null = siteData?.tinycmsSite?.siteUrl || null;
  const services: Record<string, { label: string; methods: Method[] }> = view?.services || {};
  const verifications: Verification[] = view?.verifications || [];

  const [save, { loading: saving }] = useMutation(UPDATE_TINYCMS_ROOT_FILES);
  const [restore, { loading: restoring }] = useMutation(RESTORE_TINYCMS_ROOT_FILES);

  // Adding a verification.
  const [adding, setAdding] = useState(false);
  const [service, setService] = useState('google');
  const [method, setMethod] = useState<Method>('meta');
  const [pasted, setPasted] = useState('');
  const [addError, setAddError] = useState('');

  // ads.txt / app-ads.txt.
  const [ads, setAds] = useState('');
  const [appAds, setAppAds] = useState('');
  const [adsMessage, setAdsMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => {
    setAds(view?.adsTxt || '');
    setAppAds(view?.appAdsTxt || '');
  }, [view?.adsTxt, view?.appAdsTxt]);

  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [showAll, setShowAll] = useState(false);

  if (loading && !view) return <Loader label="Loading" className="p-6 text-cmp-ink-2" />;

  const methods = services[service]?.methods || ['meta'];
  const replaces =
    !(service === 'google' && method === 'file') &&
    verifications.some((v) => v.service === service && v.method === method);
  const full = (path?: string) => (path && siteUrl ? `${siteUrl}${path}` : path || '');

  const pickService = (next: string) => {
    setService(next);
    const m = services[next]?.methods || ['meta'];
    if (!m.includes(method)) setMethod(m[0]);
    setAddError('');
  };

  const saveVerifications = async (list: Verification[], note: string) => {
    setAdsMessage(null);
    const { data: d } = await save({
      variables: { accountId, input: { verifications: list.map(stored) }, note },
    });
    await refetch();
    return d?.updateTinycmsRootFiles;
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');
    setMessage(null);
    if (!pasted.trim()) return setAddError('Paste what the service gave you.');
    const label = services[service]?.label || service;
    try {
      await saveVerifications(
        [...verifications, { service, method, value: pasted.trim() }],
        `Added ${label} (${METHOD_LABEL[method]})`
      );
      setPasted('');
      setAdding(false);
      setMessage({
        tone: 'ok',
        text: `${label} is set. It’s live now — go back to ${label} and press Verify.`,
      });
    } catch (err: any) {
      // The server's message names the problem; drop the internal list index.
      const text = String(err.message).replace(/^verifications\[\d+\]:?\s*/, '');
      setAddError(text.charAt(0).toUpperCase() + text.slice(1));
    }
  };

  const handleRemove = async (v: Verification) => {
    const label = v.label || services[v.service]?.label || v.service;
    if (
      !window.confirm(
        `Remove the ${label} ${METHOD_LABEL[v.method].toLowerCase()}? ${label} may stop treating the site as yours.`
      )
    )
      return;
    setMessage(null);
    try {
      await saveVerifications(
        verifications.filter((x) => x !== v),
        `Removed ${label} (${METHOD_LABEL[v.method]})`
      );
      setMessage({ tone: 'ok', text: `Removed ${label}.` });
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const adsDirty = ads !== (view?.adsTxt || '') || appAds !== (view?.appAdsTxt || '');
  const handleSaveAds = async () => {
    setAdsMessage(null);
    try {
      await save({
        variables: {
          accountId,
          input: { adsTxt: ads.trim() ? ads : null, appAdsTxt: appAds.trim() ? appAds : null },
          note: 'Updated ads.txt',
        },
      });
      await refetch();
      setAdsMessage({ tone: 'ok', text: 'Saved. Live on your site now.' });
    } catch (err: any) {
      setAdsMessage({ tone: 'error', text: err.message });
    }
  };

  const handleRestore = async (version: number) => {
    if (
      !window.confirm(
        `Restore version ${version}? It replaces what’s live now, and is saved as a new version.`
      )
    )
      return;
    setMessage(null);
    setAdsMessage(null);
    try {
      await restore({ variables: { accountId, version } });
      await refetch();
      setMessage({ tone: 'ok', text: `Restored version ${version}. Live on your site now.` });
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const shownVersions = showAll ? versions : versions.slice(0, 5);
  const savedAds = Boolean(view?.adsTxt || view?.appAdsTxt);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* ── Verification ── */}
      <div className="ink-site-card">
        <div className="ink-site-row">
          <span>
            <span className="ink-site-row-title">Site verification</span>
            <span className="ink-site-row-desc" style={{ display: 'block' }}>
              Proves the site is yours to Google Search Console, Bing, Pinterest, Meta or Yandex.
              Google Analytics needs nothing here — its ID goes under Analytics &amp; code.
            </span>
          </span>
          {canManage && !adding ? (
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => {
                setAdding(true);
                setAddError('');
                setMessage(null);
              }}
            >
              Add
            </button>
          ) : (
            <span />
          )}
        </div>

        {adding ? (
          <form
            onSubmit={handleAdd}
            style={{
              display: 'grid',
              gap: 12,
              padding: '14px 0',
              borderBottom: '1px solid var(--ink-line2)',
            }}
          >
            <label className="ink-site-label">
              Service
              <select
                className="ink-site-input"
                value={service}
                onChange={(e) => pickService(e.target.value)}
              >
                {Object.entries(services).map(([key, s]) => (
                  <option key={key} value={key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            {methods.length > 1 ? (
              <div className="ink-site-label">
                How {services[service]?.label} asked you to verify
                <span className="ink-seg" style={{ alignSelf: 'flex-start' }}>
                  {methods.map((m) => (
                    <button
                      key={m}
                      type="button"
                      data-active={method === m}
                      onClick={() => {
                        setMethod(m);
                        setAddError('');
                      }}
                    >
                      {METHOD_LABEL[m]}
                    </button>
                  ))}
                </span>
              </div>
            ) : null}
            <label className="ink-site-label">
              {method === 'file' ? 'The file it gave you' : 'The tag it gave you'}
              <input
                className="ink-site-input"
                style={{ fontFamily: 'var(--ink-mono)', fontSize: 13 }}
                value={pasted}
                autoFocus
                placeholder={placeholder(service, method)}
                onChange={(e) => setPasted(e.target.value)}
              />
              <span className="ink-site-row-desc" style={{ margin: 0, fontWeight: 400 }}>
                {method === 'file'
                  ? 'Paste the file name or its address. You don’t need to upload anything.'
                  : 'Paste the whole tag, or just the code inside it.'}
                {replaces ? ` This replaces the ${services[service]?.label} tag you have now.` : ''}
              </span>
            </label>
            {addError ? (
              <p className="ink-site-hint" style={{ color: 'var(--ink-red)', padding: 0 }}>
                {addError}
              </p>
            ) : null}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" className="ink-btn ink-btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="ink-btn"
                onClick={() => {
                  setAdding(false);
                  setPasted('');
                  setAddError('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {verifications.map((v, i) => (
          <div key={`${v.service}:${v.method}:${i}`} className="ink-site-row">
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="ink-site-row-title">{v.label || v.service}</span>
                <span className="ink-site-pill">{METHOD_LABEL[v.method]}</span>
              </div>
              {v.method === 'file' ? (
                <div
                  className="ink-site-row-desc"
                  style={{ fontFamily: 'var(--ink-mono)', overflowWrap: 'anywhere' }}
                >
                  {full(v.address)}
                </div>
              ) : (
                <div className="ink-site-row-desc">
                  On your home page ·{' '}
                  <span style={{ fontFamily: 'var(--ink-mono)', overflowWrap: 'anywhere' }}>
                    {v.value}
                  </span>
                </div>
              )}
            </div>
            <span style={{ display: 'flex', gap: 12 }}>
              {v.method === 'file' && siteUrl && v.address ? (
                <a
                  className="ink-site-link"
                  href={full(v.address)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open
                </a>
              ) : null}
              {canManage ? (
                <button
                  type="button"
                  className="ink-site-link"
                  style={{ color: 'var(--ink-red)' }}
                  disabled={saving}
                  onClick={() => handleRemove(v)}
                >
                  Remove
                </button>
              ) : null}
            </span>
          </div>
        ))}
        {!verifications.length && !adding ? (
          <p className="ink-site-hint" style={{ paddingTop: 0 }}>
            Nothing yet. When a service asks you to verify your site, choose Add and paste what it
            gives you.
          </p>
        ) : null}
        {message ? (
          <p
            className="ink-site-hint"
            style={{ color: message.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green-ink)' }}
          >
            {message.text}
          </p>
        ) : null}
      </div>

      {/* ── ads.txt ── */}
      <div className="ink-site-card">
        <div className="ink-site-row">
          <span>
            <span className="ink-site-row-title">ads.txt and app-ads.txt</span>
            <span className="ink-site-row-desc" style={{ display: 'block' }}>
              {adsAllowed
                ? 'If you show ads, your ad network gives you these lines. They list who may sell ad space on your site.'
                : 'For sites that show ads. Comes with the Pro plan.'}
            </span>
          </span>
          {!adsAllowed && canManage ? (
            <Link to={PLAN_LINK} className="ink-btn">
              Change plan
            </Link>
          ) : (
            <span />
          )}
        </div>
        {!adsAllowed && savedAds ? (
          <p className="ink-bill-note" data-tone="warn" style={{ margin: '14px 0' }}>
            Your ads.txt is kept, but it’s paused: your site doesn’t show it on this plan. It comes
            back when you move back to Pro.
          </p>
        ) : null}
        {adsAllowed ? (
          <div style={{ display: 'grid', gap: 14, padding: '14px 0' }}>
            {(
              [
                ['ads.txt', '/ads.txt', ads, setAds, view?.adsTxt],
                ['app-ads.txt', '/app-ads.txt', appAds, setAppAds, view?.appAdsTxt],
              ] as const
            ).map(([name, path, value, setValue, live]) => (
              <label key={name} className="ink-site-label">
                <span style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                  {name}
                  {name === 'app-ads.txt' ? (
                    <span className="ink-site-row-desc" style={{ margin: 0, fontWeight: 400 }}>
                      Only if you have a phone app with ads
                    </span>
                  ) : null}
                  {live && view?.adsServed && siteUrl ? (
                    <a
                      className="ink-site-link"
                      style={{ marginLeft: 'auto' }}
                      href={`${siteUrl}${path}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open {name}
                    </a>
                  ) : null}
                </span>
                <textarea
                  className="ink-site-input"
                  style={{ fontFamily: 'var(--ink-mono)', fontSize: 13 }}
                  rows={name === 'ads.txt' ? 6 : 3}
                  value={value}
                  readOnly={!canManage}
                  placeholder="google.com, pub-0000000000000000, DIRECT, f08c47fec0942fa0"
                  onChange={(e) => setValue(e.target.value)}
                />
              </label>
            ))}
            {canManage && adsDirty ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  disabled={saving}
                  onClick={handleSaveAds}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="ink-btn"
                  onClick={() => {
                    setAds(view?.adsTxt || '');
                    setAppAds(view?.appAdsTxt || '');
                    setAdsMessage(null);
                  }}
                >
                  Discard
                </button>
              </div>
            ) : null}
            {adsMessage ? (
              <p
                className="ink-site-hint"
                style={{
                  padding: 0,
                  color: adsMessage.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green-ink)',
                  whiteSpace: 'pre-line',
                }}
              >
                {adsMessage.text}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* ── History ── */}
      <div className="ink-site-card">
        <div className="ink-site-row">
          <span>
            <span className="ink-site-row-title">History</span>
            <span className="ink-site-row-desc" style={{ display: 'block' }}>
              Every change is saved. Restoring puts an earlier version live again.
            </span>
          </span>
          <span />
        </div>
        {shownVersions.map((v) => (
          <div key={v.version} className="ink-site-row">
            <div style={{ minWidth: 0 }}>
              <div className="ink-site-row-title" style={{ fontSize: 14 }}>
                Version {v.version}
                {v.note ? ` · ${v.note}` : ''}
              </div>
              <div className="ink-site-row-desc">
                {when(v.changedAt)} ·{' '}
                {v.changedBy || (v.changedVia === 'mcp' ? 'an agent' : 'unknown')}
                {v.changedVia === 'mcp' ? ' (agent)' : ''}
              </div>
            </div>
            {v.version === view?.version ? (
              <span className="ink-site-pill">Live</span>
            ) : canManage ? (
              <button
                type="button"
                className="ink-site-link"
                disabled={restoring}
                onClick={() => handleRestore(v.version)}
              >
                Restore
              </button>
            ) : (
              <span />
            )}
          </div>
        ))}
        {!versions.length ? (
          <p className="ink-site-hint" style={{ paddingTop: 0 }}>
            No changes yet.
          </p>
        ) : null}
        {versions.length > 5 ? (
          <div className="ink-site-hint">
            <button type="button" className="ink-site-link" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show fewer' : `Show all ${versions.length}`}
            </button>
          </div>
        ) : null}
        {!canManage ? (
          <p className="ink-site-hint">Only an account admin can change these.</p>
        ) : null}
      </div>
    </div>
  );
}
